// Dev/rehearsal-only: fakes `credits_balance` in every GraphQL response so any credit state is
// reachable without a matching IFE account, e.g. `?mock_credits=empty` or `?mock_credits=low:8/250`.

import type { CreditsBalance, CreditsBalanceStatus } from "shared/utils/credits"

interface GraphResponse {
  data?: unknown
}

type ParseResult = { balance: CreditsBalance } | { error: string }

const PARAM = "mock_credits"
const MONTHLY_TOTAL = 100
const REHEARSAL_HOST = "web-rehearsal.ifttt.com"

const REMAINING_BY_STATUS: Record<CreditsBalanceStatus, number> = {
  ok: 80,
  moderate: 42,
  low: 20,
  very_low: 5,
  empty: 0,
}

// Checks the Rails environment the page runs in rather than the build mode, so rehearsal — which
// builds for production — is covered too.
export const creditsMockEnabled = () => window.App?.env === "development" || window.location.hostname === REHEARSAL_HOST

const isStatus = (value: string): value is CreditsBalanceStatus =>
  Object.prototype.hasOwnProperty.call(REMAINING_BY_STATUS, value)

const currentParam = (): string | null => new URLSearchParams(window.location.search).get(PARAM)

const nextResetDate = () => {
  const now = new Date()

  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString()
}

// The schema's amounts are non-null `Int`, so reject bad input rather than coerce it like `Number` would.
const parseAmount = (value: string): number =>
  /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) ? Number(value) : NaN

const parseAmounts = (amounts: string): { remaining: number; monthlyTotal: number } | { error: string } => {
  const parts = amounts.split("/")
  if (parts.length !== 2) {
    return { error: `"${amounts}" is not a remaining/monthly total pair, as in 8/250` }
  }

  const [remaining, monthlyTotal] = parts.map(parseAmount)
  if (Number.isNaN(remaining) || Number.isNaN(monthlyTotal)) {
    return { error: `"${amounts}" is not a pair of whole numbers, as in 8/250` }
  }
  if (remaining > monthlyTotal) {
    return { error: `"${amounts}" leaves more credits remaining than the monthly total` }
  }

  return { remaining, monthlyTotal }
}

const buildBalance = (status: CreditsBalanceStatus, remaining: number, monthlyTotal: number): CreditsBalance => ({
  remaining,
  monthly_total: monthlyTotal,
  spent: monthlyTotal - remaining,
  reset_date: nextResetDate(),
  status,
})

const parseBalance = (param: string): ParseResult => {
  const [status, amounts, ...rest] = param.split(":")
  if (rest.length > 0) {
    return { error: `"${param}" has more than one ":"; expected a status or status:remaining/monthly total` }
  }
  if (!isStatus(status)) {
    return {
      error: `"${status}" is not a known status; expected one of ${Object.keys(REMAINING_BY_STATUS).join(", ")}`,
    }
  }

  if (amounts === undefined) {
    return { balance: buildBalance(status, REMAINING_BY_STATUS[status], MONTHLY_TOTAL) }
  }
  if (!amounts) {
    return { error: `"${param}" has an empty amount; expected a pair as in ${status}:8/250` }
  }

  const parsed = parseAmounts(amounts)
  if ("error" in parsed) return parsed

  return { balance: buildBalance(status, parsed.remaining, parsed.monthlyTotal) }
}

const mockCredits = (): { credits_balance: CreditsBalance } | null => {
  const param = currentParam()
  if (!param) return null

  const parsed = parseBalance(param)
  if ("error" in parsed) {
    console.warn(`[mock_credits] ${parsed.error}`)
    return null
  }

  return { credits_balance: parsed.balance }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

// Keeps the original's keys so a query that asked for a subset of the fields gets only that subset.
const overwrite = (original: unknown, replacement: unknown): unknown => {
  if (!isRecord(original) || !isRecord(replacement)) return replacement

  return Object.fromEntries(
    Object.keys(original).map(key => [key, key in replacement ? replacement[key] : original[key]]),
  )
}

// Walks the whole response rather than just `data.me`, so a balance under any type or nesting is covered.
const patch = (node: unknown, mocked: Record<string, unknown>): void => {
  if (Array.isArray(node)) {
    node.forEach(entry => patch(entry, mocked))
    return
  }
  if (!isRecord(node)) return

  Object.keys(node).forEach(key => {
    if (key in mocked) node[key] = overwrite(node[key], mocked[key])
    else patch(node[key], mocked)
  })
}

export const applyCreditsMock = (result: GraphResponse): GraphResponse => {
  if (!isRecord(result?.data)) return result

  const mocked = mockCredits()
  if (!mocked) return result

  patch(result.data, mocked)

  return result
}
